import React from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {Route, useParams, useRouteMatch} from 'react-router-dom';
import {toast} from 'react-toastify';

import {downloadMarkingCOdesByTINS} from 'api';
import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {Button} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {
    ContentColumns,
    ContentContainer,
    CopyButton,
    Id,
    StatusWrapper,
    Title,
    TitleText,
    TitleWrapper,
} from 'components/ui-kit/PageComponents';
import {RouterSwitch, RouterTab, RouterTabList} from 'components/ui-kit/RouterTabs';
import {ORDER_STATUS_COLORS} from 'core/constants/statuses';
import {OrderStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {commonDataState$, Gate, productsTableState$} from 'models/orders/detail';
import {colors} from 'theme';
import {OrderDetailFormatted, OrderDetailRaw} from 'types/orders';
import {useClipboard} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {PrintModal} from './Print';
import {Products} from './Products';
import {DetailsWrapper, Error, ErrorsWrapper} from './styled';

function getStatusColors(status?: OrderStatus) {
    return {color: ORDER_STATUS_COLORS[status ?? 'DEFAULT']};
}

export function convertData(data: OrderDetailRaw | null, t: TFunction): OrderDetailFormatted {
    return {
        orderId: handleNil(data?.orderId),
        createdAt: handleNil(data?.createdAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy, HH:mm'),
        ),
        status: handleNil(data?.status, (value: OrderStatus) => t(`dictionaries.orders.${value}`)),
        totalMarkingCodes: handleNil(data?.totalMarkingCodes),
        factoryId: handleNil(data?.factoryId),
        country: handleNil(data?.country),
        productionLineId: handleNil(data?.productionLineId),
        availableCodes: handleNil(data?.availableCodes),
        errors: data?.errors
            ? data.errors.map(
                  (err) => `${t('pages.orders.detail.error')} #${err.code}. ${err.message}`,
              )
            : undefined,
    };
}

export const OrdersDetailPage = () => {
    const {t} = useTranslation();
    const {path, url} = useRouteMatch();
    const {id}: {id: string} = useParams();
    const {data, error, isLoading} = useStore(commonDataState$);
    const {data: productRows} = useStore(productsTableState$);
    useGate(Gate, {id});
    const copy = useClipboard();

    const mappedData = React.useMemo(() => convertData(data, t), [data, t]);

    const copyCode = () => {
        copy(mappedData.orderId);
        toast.success(t('pages.orders.detail.idCopied'));
    };

    const onDownloadXLS = async () => {
        try {
            const gtin = productRows?.[0].gtin;
            const response = await downloadMarkingCOdesByTINS({gtin, orderId: id});
            saveAs(response, `marking_codes_${id}_${gtin}.xls`);
        } catch (err) {
            console.error('Error:', err);
        }
    };

    return (
        <>
            <Page title={t('pages.orders.detail.orderTitle')}>
                <BackTo to={ROUTE.ORDERS_GRID}>{t('pages.orders.back')}</BackTo>
                <ContentContainer
                    status={isLoading ? 'loading' : error != null ? 'error' : 'default'}
                >
                    <TitleWrapper>
                        <TitleText>
                            <Title>
                                {t('pages.orders.detail.orderId')} <Id>{mappedData.orderId}</Id>
                            </Title>
                            <CopyButton
                                icon={
                                    <Icon
                                        source={CopyIcon}
                                        width={15}
                                        height={15}
                                        fillColor={colors.brandPrimary}
                                    />
                                }
                                onClick={copyCode}
                                hoverIndication={true}
                                label={t('pages.orders.detail.common.copyId')}
                                labelPlace={'bottom'}
                            />
                        </TitleText>
                        <DetailsWrapper>
                            {/*<Button onClick={onDownloadXLS}>*/}
                            {/*    {t('pages.orders.detail.common.download')}*/}
                            {/*</Button>*/}
                            <StatusWrapper
                                style={{flexShrink: 0}}
                                {...getStatusColors(data?.status)}
                            >
                                {mappedData.status}
                            </StatusWrapper>
                        </DetailsWrapper>
                    </TitleWrapper>
                    {mappedData.errors && (
                        <ErrorsWrapper>
                            {mappedData.errors.map((err, index) => (
                                <Error key={index}>{err}</Error>
                            ))}
                        </ErrorsWrapper>
                    )}
                    <ContentColumns
                        data={[
                            {createdAt: mappedData.createdAt},
                            {totalMarkingCodes: mappedData.totalMarkingCodes},
                            {availableCodes: mappedData.availableCodes},
                        ]}
                        tNameSpace="pages.orders.detail"
                    />

                    <RouterTabList baseUrl={url}>
                        <RouterTab url="">{t('pages.orders.detail.commonInfo')}</RouterTab>
                        <RouterTab url="products">{t('pages.orders.detail.products')}</RouterTab>
                    </RouterTabList>

                    <RouterSwitch rootPath={path}>
                        <Route path={`${path}`} exact>
                            <ContentColumns
                                data={[
                                    {country: mappedData.country},
                                    {
                                        factoryId: mappedData.factoryId,
                                        productionLineId: mappedData.productionLineId,
                                    },
                                ]}
                                tNameSpace="pages.orders.detail.common"
                            />
                        </Route>
                        <Route path={`${path}/products`} exact>
                            <Products />
                        </Route>
                    </RouterSwitch>
                </ContentContainer>
            </Page>
            <PrintModal />
        </>
    );
};
